/**
 * Shared DIMENSION primitive-geometry builder (Lane H / S8).
 *
 * Extracted from `../../dimensions/dimensionToPrimitives.ts` so the LEGACY
 * bag adapter and the TYPED adapter (`../typed/dimensionFromEntity.ts`) share
 * ONE geometry truth: both extract the same handful of raw numeric fields
 * (from `EntityPreview.properties` vs. the canonical `DimensionEntity`
 * respectively) and feed them here. Mirrors the `hatchToRenderable.ts` /
 * `../geom/hatchGeometry.ts` split.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * Subtype semantics, DIMASZ / DIMTXT / DIMGAP / DIMEXO / DIMEXE / DIMSE1/2 /
 * DIMTIH / DIMBLK gating are byte-identical to the pre-extraction inline code
 * (ported verbatim from `renderDimension.tsx` originally) — this is a pure
 * behaviour-preserving extraction, not a rewrite.
 */

import {
  formatDimMeasurement,
  computeToleranceInfo,
  pickDimensionAnnotativeContext,
  type DimStyleValues,
} from "../../../scene/dimensionHelpers";
import type {
  DimensionPrimitive,
  SceneGeometry,
  TextAlignment,
  Vec3,
} from "../../types";
import { dimblkToVariant } from "../../dimensions/arrowMarkerMesh";
import { applyOffset } from "../../coordinateOrigin";
import { positiveOr } from "../adapterContext";
import { unitVec2, rotatedBoxCorners, type RawVec2 } from "./vecMath";

export type { RawVec2 };

/**
 * Raw geometric inputs a DIMENSION subtype emitter needs, extracted from
 * EITHER source (legacy bag string keys or typed `DimensionEntity` fields).
 * `textHeightRaw` is the PRE-CLAMP value (`entity.textHeight` /
 * `readNumber(props, "Text height")`, both default 0) — the shared builder
 * applies the SAME `positiveOr(…, style.dimtxt)` clamp both paths used, so
 * the clamp logic itself cannot drift between the two extractors.
 */
export interface DimensionRawFields {
  ext1: RawVec2;
  ext2: RawVec2;
  dimLine: RawVec2;
  thirdPoint: RawVec2;
  measurement: number;
  /** `entity.dimensionText || "<>"` — identical expression both paths use. */
  textOverride: string;
  textHeightRaw: number;
  /**
   * `entity.textAngleRelativeToDimLine` (DXF group 53, decimal degrees) —
   * ezdxf's real `text_rotation`: a manual override the dimension text's
   * rendered angle is offset by, ADDITIVE to whatever DIMTIH / dimline-
   * derived angle the subtype would otherwise use (0 for every subtype but
   * Aligned; the Aligned dimline angle when DIMTIH=0). Defaults 0 so an
   * un-overridden dimension renders byte-identical to before this field
   * existed. GitHub Codex review PR #558 (post-rebase fresh pass) P1 — this
   * is also the field MIRRTEXT now mirrors for Dimension
   * (`geometryReducerEdit.ts`'s `geometry/mirror-entities` case); wiring it
   * here is what makes MIRRTEXT=0 vs MIRRTEXT=1 render differently for a
   * mirrored Dimension, closing the gap Codex found (the reducer computed a
   * value nothing consumed).
   */
  textRotationOverride: number;
  /**
   * GitHub Codex review PR #558 (post-rebase fresh pass) P1 — handedness
   * companion to `textRotationOverride`, sourced from `DimensionEntity.
   * textMirrored`.
   */
  textMirrored: boolean;
}

export interface DimensionBbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface EmitResult {
  primitives: DimensionPrimitive[];
  bbox: DimensionBbox;
}

/**
 * Both render feed builders (`buildDimensionPreview` in `state/dxfGeometry.ts`
 * and, by omission, the typed adapter) never populate a `"Text style"` /
 * per-dimstyle text-style-name bag key for DIMENSION — `readDimStyle`'s
 * legacy branch always falls through to the `"Standard"` literal. Golden
 * parity requires the typed path to reproduce that (pre-existing, out of
 * Lane H scope) rather than resolve a real DIMTXSTY.
 */
const DIMENSION_TEXT_STYLE_NAME = "Standard";

function vec3(x: number, y: number, z = 0): Vec3 {
  return { x, y, z };
}

/** Tick (slash) arrow-block variants vs. a regular triangle/dot/open arrow. */
function isTickBlock(blockName: string): boolean {
  return blockName === "_ARCHTICK" || blockName === "_OBLIQUE";
}

/** DIMBLK1/DIMBLK2 override DIMBLK when set (mirrors AutoCAD's resolution). */
function resolveArrowBlocks(style: DimStyleValues): {
  start: string;
  end: string;
} {
  return {
    start: style.dimblk1 || style.dimblk,
    end: style.dimblk2 || style.dimblk,
  };
}

function extendBbox(bbox: DimensionBbox, p: Vec3): void {
  if (p.x < bbox.minX) bbox.minX = p.x;
  if (p.y < bbox.minY) bbox.minY = p.y;
  if (p.x > bbox.maxX) bbox.maxX = p.x;
  if (p.y > bbox.maxY) bbox.maxY = p.y;
}

function emptyBbox(): DimensionBbox {
  return {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };
}

export function finalizeDimensionBbox(
  bbox: DimensionBbox,
  fallback: Vec3,
): DimensionBbox {
  if (!Number.isFinite(bbox.minX)) {
    return {
      minX: fallback.x,
      minY: fallback.y,
      maxX: fallback.x,
      maxY: fallback.y,
    };
  }
  return bbox;
}

function emitArrowOrTick(
  out: DimensionPrimitive[],
  tip: Vec3,
  direction: Vec3,
  blockName: string,
  size: number,
  colorHex: string,
): void {
  if (blockName === "_NONE") return;
  if (isTickBlock(blockName)) {
    out.push({
      kind: "tick",
      tip,
      direction,
      size,
      variant: blockName === "_OBLIQUE" ? "oblique" : "arch-tick",
      colorHex,
    });
    return;
  }
  out.push({
    kind: "arrow",
    tip,
    direction,
    size,
    variant: dimblkToVariant(blockName),
    colorHex,
  });
}

/**
 * Shared "arrow at each end of a straight dim-line segment" emitter —
 * IDENTICAL shape across Linear / Aligned / Diameter (jscpd dup-gate
 * extraction; behaviour-preserving, byte-identical to the 3 inline copies
 * this replaces). Angular uses tangent directions (not endpoint-to-endpoint)
 * and Radius emits only ONE end — both stay bespoke.
 */
function emitArrowPair(
  primitives: DimensionPrimitive[],
  a: Vec3,
  b: Vec3,
  style: DimStyleValues,
  colorHex: string,
): void {
  const arrows = resolveArrowBlocks(style);
  const dirAB = unitVec2(b.x - a.x, b.y - a.y);
  emitArrowOrTick(primitives, a, vec3(-dirAB.x, -dirAB.y), arrows.start, style.dimasz, colorHex);
  emitArrowOrTick(primitives, b, vec3(dirAB.x, dirAB.y), arrows.end, style.dimasz, colorHex);
}

/**
 * Shared "suppressible extension-line + bbox extend" emitter — the
 * `if (!style.dimseN) { push; extend×2 }` wrapper repeats identically
 * across Linear / Aligned / Angular's two extension lines each (jscpd
 * dup-gate extraction); only the endpoint MATH differs per subtype, which
 * callers compute before invoking this.
 */
function emitExtensionLine(
  primitives: DimensionPrimitive[],
  bbox: DimensionBbox,
  suppress: boolean,
  a: Vec3,
  b: Vec3,
  colorHex: string,
): void {
  if (suppress) return;
  primitives.push({ kind: "extension-line", a, b, colorHex });
  extendBbox(bbox, a);
  extendBbox(bbox, b);
}

/**
 * Shared "measurement text primitive + half-width bbox extend" emitter —
 * IDENTICAL shape across every subtype (jscpd dup-gate extraction). Angular
 * passes `content = text + "°"`; `content.length` naturally picks up that
 * extra glyph's width, matching the original inline `(text.length + 1)`
 * computation byte-for-byte. No-op when `content` is empty (tolerance mode
 * "limits" suppresses the text primitive entirely on every subtype).
 */
function emitMeasurementText(
  primitives: DimensionPrimitive[],
  bbox: DimensionBbox,
  position: Vec3,
  rotation: number,
  content: string,
  textHeight: number,
  colorHex: string,
  mirrored: boolean,
): void {
  if (!content) return;
  primitives.push({
    kind: "text",
    position,
    rotation,
    height: textHeight,
    content,
    styleName: DIMENSION_TEXT_STYLE_NAME,
    alignment: "baseline-center" satisfies TextAlignment,
    colorHex,
    mirrored,
  });
  // GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up)
  // P2 — a nonzero `rotation` (group 53, degrees) now actually rotates the
  // rendered glyphs (`dimensionSubtypeTextRotationDeg`), so the bbox must
  // follow: extend it with the text box's ROTATED corners, not an
  // axis-aligned box. `RenderableEntity.bounds` (built from this bbox)
  // drives spatial-index culling and pick/window selection — an unrotated
  // bbox under a 90°-rotated label lets the glyphs render outside their
  // reported bounds and disappear / become unselectable.
  //
  // Local box (relative to `position`, BEFORE rotation): X is symmetric
  // (`-halfX`..`+halfX`, unaffected by mirroring). Y is ASYMMETRIC —
  // "baseline-center" alignment normally stacks the glyph ABOVE the anchor
  // (`0`..`+textHeight`), but a MIRRTEXT=1 mirrored Dimension applies a
  // local-Y-flip to the same glyph (`applyMirrorScale`'s render convention),
  // which stacks it BELOW instead (`-textHeight`..`0`).
  const halfX = Math.max(content.length * textHeight * 0.3, textHeight);
  const rotationRad = (rotation * Math.PI) / 180;
  const localMinY = mirrored ? -textHeight : 0;
  const localMaxY = mirrored ? 0 : textHeight;
  const corners = rotatedBoxCorners(
    position,
    -halfX,
    halfX,
    localMinY,
    localMaxY,
    rotationRad,
  );
  for (const c of corners) extendBbox(bbox, vec3(c.x, c.y));
}

/**
 * Shared subtype-emitter preamble — clamps text height, resolves the
 * measurement text ("limits" mode → `""`), hands back fresh empty
 * `primitives[]` / `bbox` accumulators, AND pre-destructures `ext1` /
 * `ext2` / `dimLine` (every raw-field group at least one subtype reads;
 * Radius/Ordinate simply never touch the returned `x2`/`y2`). Every
 * emitter opened with this IDENTICAL sequence (jscpd dup-gate extraction —
 * folded into ONE call instead of ~6 separate statements). `prefix` is the
 * subtype's measurement-symbol (`"⌀"` Diameter, `"R"` Radius, absent for
 * the rest). Only Angular also needs `thirdPoint` — destructured locally,
 * genuinely subtype-specific.
 */
function beginEmit(
  raw: DimensionRawFields,
  style: DimStyleValues,
  prefix?: string,
): {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  dx: number;
  dy: number;
  textHeight: number;
  text: string;
  primitives: DimensionPrimitive[];
  bbox: DimensionBbox;
} {
  const textHeight = positiveOr(raw.textHeightRaw, style.dimtxt);
  const tol = computeToleranceInfo(raw.measurement, style);
  const text =
    tol.mode === "limits"
      ? ""
      : formatDimMeasurement(raw.textOverride, raw.measurement, style, prefix);
  return {
    x1: raw.ext1.x,
    y1: raw.ext1.y,
    x2: raw.ext2.x,
    y2: raw.ext2.y,
    dx: raw.dimLine.x,
    dy: raw.dimLine.y,
    textHeight,
    text,
    primitives: [],
    bbox: emptyBbox(),
  };
}

function emitLinear(
  raw: DimensionRawFields,
  style: DimStyleValues,
  colorHex: string,
): EmitResult {
  const { x1, y1, x2, y2, dx, dy, textHeight, text, primitives, bbox } = beginEmit(raw, style);

  const sign1 = Math.sign(dy - y1) || 1;
  const sign2 = Math.sign(dy - y2) || 1;
  const ext1Start = applyOffset(vec3(x1, y1 + sign1 * style.dimexo));
  const ext1End = applyOffset(vec3(x1, dy + sign1 * style.dimexe));
  const ext2Start = applyOffset(vec3(x2, y2 + sign2 * style.dimexo));
  const ext2End = applyOffset(vec3(x2, dy + sign2 * style.dimexe));

  emitExtensionLine(primitives, bbox, style.dimse1, ext1Start, ext1End, colorHex);
  emitExtensionLine(primitives, bbox, style.dimse2, ext2Start, ext2End, colorHex);

  const dimA = applyOffset(vec3(x1, dy));
  const dimB = applyOffset(vec3(x2, dy));
  primitives.push({ kind: "dim-line", a: dimA, b: dimB, colorHex });
  extendBbox(bbox, dimA);
  extendBbox(bbox, dimB);

  emitArrowPair(primitives, dimA, dimB, style, colorHex);

  const textXPos = dx || (x1 + x2) / 2;
  const textY = dy + style.dimgap + textHeight * 0.3;
  const textPos = applyOffset(vec3(textXPos, textY));
  emitMeasurementText(
    primitives,
    bbox,
    textPos,
    raw.textRotationOverride,
    text,
    textHeight,
    colorHex,
    raw.textMirrored,
  );

  return { primitives, bbox };
}

function emitAligned(
  raw: DimensionRawFields,
  style: DimStyleValues,
  colorHex: string,
): EmitResult {
  const { x1, y1, x2, y2, dx, dy, textHeight, text, primitives, bbox } = beginEmit(raw, style);

  const lineAngle = Math.atan2(y2 - y1, x2 - x1);
  const perpX = -Math.sin(lineAngle);
  const perpY = Math.cos(lineAngle);

  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const offsetDist = (dx - midX) * perpX + (dy - midY) * perpY;
  const sign = offsetDist >= 0 ? 1 : -1;

  const dim1x = x1 + perpX * offsetDist;
  const dim1y = y1 + perpY * offsetDist;
  const dim2x = x2 + perpX * offsetDist;
  const dim2y = y2 + perpY * offsetDist;

  emitExtensionLine(
    primitives,
    bbox,
    style.dimse1,
    applyOffset(vec3(x1 + perpX * style.dimexo * sign, y1 + perpY * style.dimexo * sign)),
    applyOffset(
      vec3(dim1x + perpX * style.dimexe * sign, dim1y + perpY * style.dimexe * sign),
    ),
    colorHex,
  );
  emitExtensionLine(
    primitives,
    bbox,
    style.dimse2,
    applyOffset(vec3(x2 + perpX * style.dimexo * sign, y2 + perpY * style.dimexo * sign)),
    applyOffset(
      vec3(dim2x + perpX * style.dimexe * sign, dim2y + perpY * style.dimexe * sign),
    ),
    colorHex,
  );

  const dimA = applyOffset(vec3(dim1x, dim1y));
  const dimB = applyOffset(vec3(dim2x, dim2y));
  primitives.push({ kind: "dim-line", a: dimA, b: dimB, colorHex });
  extendBbox(bbox, dimA);
  extendBbox(bbox, dimB);

  emitArrowPair(primitives, dimA, dimB, style, colorHex);

  const textMidX = (dim1x + dim2x) / 2 + perpX * (style.dimgap + textHeight * 0.3);
  const textMidY = (dim1y + dim2y) / 2 + perpY * (style.dimgap + textHeight * 0.3);
  const textRot =
    (style.dimtih ? 0 : (lineAngle * 180) / Math.PI) + raw.textRotationOverride;
  const textPos = applyOffset(vec3(textMidX, textMidY));
  emitMeasurementText(
    primitives,
    bbox,
    textPos,
    textRot,
    text,
    textHeight,
    colorHex,
    raw.textMirrored,
  );

  return { primitives, bbox };
}

function emitAngular(
  raw: DimensionRawFields,
  style: DimStyleValues,
  colorHex: string,
): EmitResult {
  const { x: tx, y: ty } = raw.thirdPoint;
  const { x1, y1, x2, y2, dx, dy, textHeight, text, primitives, bbox } = beginEmit(raw, style);

  const vx = tx || dx;
  const vy = ty || dy;
  const angle1 = Math.atan2(y1 - vy, x1 - vx);
  const angle2 = Math.atan2(y2 - vy, x2 - vx);

  const arcRadius =
    Math.hypot(dx - vx, dy - vy) ||
    Math.min(Math.hypot(x1 - vx, y1 - vy), Math.hypot(x2 - vx, y2 - vy)) * 0.7;

  const arcStart = { x: vx + arcRadius * Math.cos(angle1), y: vy + arcRadius * Math.sin(angle1) };
  const arcEnd = { x: vx + arcRadius * Math.cos(angle2), y: vy + arcRadius * Math.sin(angle2) };

  emitExtensionLine(
    primitives,
    bbox,
    style.dimse1,
    applyOffset(vec3(vx + style.dimexo * Math.cos(angle1), vy + style.dimexo * Math.sin(angle1))),
    applyOffset(
      vec3(
        arcStart.x + style.dimexe * Math.cos(angle1),
        arcStart.y + style.dimexe * Math.sin(angle1),
      ),
    ),
    colorHex,
  );
  emitExtensionLine(
    primitives,
    bbox,
    style.dimse2,
    applyOffset(vec3(vx + style.dimexo * Math.cos(angle2), vy + style.dimexo * Math.sin(angle2))),
    applyOffset(
      vec3(
        arcEnd.x + style.dimexe * Math.cos(angle2),
        arcEnd.y + style.dimexe * Math.sin(angle2),
      ),
    ),
    colorHex,
  );

  const center = applyOffset(vec3(vx, vy));
  primitives.push({
    kind: "arc",
    center,
    radius: arcRadius,
    startAngle: angle1,
    endAngle: angle2,
    colorHex,
  });
  extendBbox(bbox, vec3(center.x - arcRadius, center.y - arcRadius));
  extendBbox(bbox, vec3(center.x + arcRadius, center.y + arcRadius));

  const arrows = resolveArrowBlocks(style);
  const arcStartW = applyOffset(vec3(arcStart.x, arcStart.y));
  const arcEndW = applyOffset(vec3(arcEnd.x, arcEnd.y));
  const tangentStart = vec3(-Math.sin(angle1), Math.cos(angle1));
  const tangentEnd = vec3(Math.sin(angle2), -Math.cos(angle2));
  emitArrowOrTick(primitives, arcStartW, tangentStart, arrows.start, style.dimasz, colorHex);
  emitArrowOrTick(primitives, arcEndW, tangentEnd, arrows.end, style.dimasz, colorHex);

  const midAngle =
    angle1 + ((((angle2 - angle1) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) / 2;
  const textPos = applyOffset(
    vec3(
      vx + (arcRadius + style.dimgap + textHeight * 0.3) * Math.cos(midAngle),
      vy + (arcRadius + style.dimgap + textHeight * 0.3) * Math.sin(midAngle),
    ),
  );
  emitMeasurementText(
    primitives,
    bbox,
    textPos,
    raw.textRotationOverride,
    text ? text + "°" : "",
    textHeight,
    colorHex,
    raw.textMirrored,
  );

  return { primitives, bbox };
}

function emitDiameter(
  raw: DimensionRawFields,
  style: DimStyleValues,
  colorHex: string,
): EmitResult {
  const { x1, y1, x2, y2, dx, dy, textHeight, text, primitives, bbox } = beginEmit(raw, style, "⌀");

  const a = applyOffset(vec3(x1, y1));
  const b = applyOffset(vec3(x2, y2));
  primitives.push({ kind: "dim-line", a, b, colorHex });
  extendBbox(bbox, a);
  extendBbox(bbox, b);

  emitArrowPair(primitives, a, b, style, colorHex);

  const textXPos = dx || (x1 + x2) / 2;
  const textYPos = (dy || (y1 + y2) / 2) + style.dimgap + textHeight * 0.3;
  const textPos = applyOffset(vec3(textXPos, textYPos));
  emitMeasurementText(
    primitives,
    bbox,
    textPos,
    raw.textRotationOverride,
    text,
    textHeight,
    colorHex,
    raw.textMirrored,
  );

  return { primitives, bbox };
}

function emitRadius(
  raw: DimensionRawFields,
  style: DimStyleValues,
  colorHex: string,
): EmitResult {
  const { x1, y1, dx, dy, textHeight, text, primitives, bbox } = beginEmit(raw, style, "R");

  const center = applyOffset(vec3(dx, dy));
  const arcPt = applyOffset(vec3(x1, y1));
  primitives.push({ kind: "leader", a: center, b: arcPt, colorHex });
  extendBbox(bbox, center);
  extendBbox(bbox, arcPt);

  const arrows = resolveArrowBlocks(style);
  const dir = unitVec2(arcPt.x - center.x, arcPt.y - center.y);
  emitArrowOrTick(primitives, arcPt, vec3(dir.x, dir.y), arrows.end, style.dimasz, colorHex);

  const textPos = applyOffset(
    vec3((dx + x1) / 2, (dy + y1) / 2 + style.dimgap + textHeight * 0.3),
  );
  emitMeasurementText(
    primitives,
    bbox,
    textPos,
    raw.textRotationOverride,
    text,
    textHeight,
    colorHex,
    raw.textMirrored,
  );

  return { primitives, bbox };
}

function emitOrdinate(
  raw: DimensionRawFields,
  style: DimStyleValues,
  colorHex: string,
): EmitResult {
  const { x1, y1, dx, dy, textHeight, text, primitives, bbox } = beginEmit(raw, style);

  const isXOrdinate = Math.abs(dy - y1) > Math.abs(dx - x1);
  const midX = isXOrdinate ? x1 : (x1 + dx) / 2;
  const midY = isXOrdinate ? (y1 + dy) / 2 : y1;

  const featurePt = applyOffset(vec3(x1, y1));
  const midPt = applyOffset(vec3(midX, midY));
  const labelPt = applyOffset(vec3(dx, dy));
  primitives.push({ kind: "leader", a: featurePt, b: midPt, colorHex });
  primitives.push({ kind: "leader", a: midPt, b: labelPt, colorHex });
  extendBbox(bbox, featurePt);
  extendBbox(bbox, midPt);
  extendBbox(bbox, labelPt);

  const textPos = applyOffset(vec3(dx, dy + style.dimgap + textHeight * 0.3));
  emitMeasurementText(
    primitives,
    bbox,
    textPos,
    raw.textRotationOverride,
    text,
    textHeight,
    colorHex,
    raw.textMirrored,
  );

  return { primitives, bbox };
}

/**
 * TYPED-path DIMVAR resolution — `resolveDimensionStyleRow` +
 * `dimensionStyleRowToValues` were RELOCATED to the pure
 * `scene/dimensionHelpers.ts` (codex PR #472 round-4 finding 4) so the P7
 * insertion-overlap bounds path can reuse the SAME DIMSTYLE resolution
 * without importing this three.js-laden module. Re-exported here so this
 * file's existing importers (`dimensionFromEntity.ts`) are unchanged.
 */
export {
  resolveDimensionStyleRow,
  dimensionStyleRowToValues,
} from "../../../scene/dimensionHelpers";

/**
 * Resolve the effective `DimStyleValues` for the annotative case: when the
 * dimension is annotative (DIMANNO truthy) and a matching per-scale context
 * exists, DIMTXT/DIMASZ are overridden by the context row — mirrors
 * `renderDimension.ts:704+`. `annotative` / `contexts` / `annoScale` are
 * extracted differently by each caller (bag JSON vs. typed
 * `entity.annotativeContexts`).
 */
export function applyDimensionAnnotativeOverride(
  baseStyle: DimStyleValues,
  annotative: boolean,
  contexts: ReadonlyArray<{
    scale: number;
    textHeight?: number;
    arrowheadSize?: number;
  }>,
  annoScale: number | null,
): DimStyleValues {
  if (!annotative) return baseStyle;
  const ctxRow = pickDimensionAnnotativeContext([...contexts], annoScale);
  if (!ctxRow) return baseStyle;
  return {
    ...baseStyle,
    dimtxt: ctxRow.textHeight ?? baseStyle.dimtxt,
    dimasz: ctxRow.arrowheadSize ?? baseStyle.dimasz,
  };
}

/**
 * Map the DXF `dimensionType` group-70 value (low nibble) to the emitter
 * subtype index (0=Linear … 6=Ordinate). Mirrors the legacy bag round-trip
 * `dimensionSubtypeLabel` → `parseDimensionSubtype`: any nibble outside 0-6
 * has no label (`"Unknown (N)"`) and `parseDimensionSubtype` maps an unknown
 * label back to 0 (Linear) — so an out-of-range nibble collapses to 0 here
 * too, byte-identical to the round-trip.
 */
export function resolveDimensionSubtype(dimensionType: number | undefined): number {
  const nibble = (dimensionType ?? 0) & 0x0f;
  return nibble >= 0 && nibble <= 6 ? nibble : 0;
}

/**
 * Dispatch to the per-subtype emitter and return `{primitives, bbox}` (bbox
 * NOT yet finalized against the fallback point — callers pass it through
 * {@link finalizeDimensionBbox}).
 */
export function buildDimensionGeometry(
  raw: DimensionRawFields,
  subtype: number,
  style: DimStyleValues,
  colorHex: string,
): EmitResult {
  switch (subtype) {
    case 1:
      return emitAligned(raw, style, colorHex);
    case 2:
    case 5:
      return emitAngular(raw, style, colorHex);
    case 3:
      return emitDiameter(raw, style, colorHex);
    case 4:
      return emitRadius(raw, style, colorHex);
    case 6:
      return emitOrdinate(raw, style, colorHex);
    case 0:
    default:
      return emitLinear(raw, style, colorHex);
  }
}

/** Build the final `SceneGeometry` envelope from the emitter's primitives. */
export function toDimensionSceneGeometry(
  primitives: DimensionPrimitive[],
): SceneGeometry {
  return {
    kind: "dimension",
    primitives,
    // Pre-existing gap (both paths): the bag never carried a per-dimension
    // "Style" key (only "Dimension style", a DIFFERENT key `readDimStyle`
    // never reads), so `geometry.styleName` has always been the "Standard"
    // literal regardless of the referenced DIMSTYLE. Out of Lane H scope —
    // reproduced verbatim for golden parity.
    styleName: DIMENSION_TEXT_STYLE_NAME,
  };
}
